/* eslint-disable @typescript-eslint/unbound-method */
// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Redirect, withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';

import { UserDO } from 'wdna-components/lib/types.new';
import { messageError } from 'wdna-components/lib/helpers/notification';

import { RootState } from '../../../reducers';

import Auth from '../../../services/auth/Auth';
import { authorization, AxiosRequestConfig } from '../requests';

import {
  version as currentVersion
} from '../../../../../package.json';

interface Props extends RouteComponentProps {
  user: UserDO | undefined;
}

interface State {
  username?: string;
  password?: string;
  loading?: boolean;
}

class Login extends React.PureComponent<Props, State> {
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.mounted = false;

    this.state = {
      username: '',
      password: '',
      loading: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
  }

  public componentDidMount(): void {
    this.mounted = true;
    if (Auth.getToken() !== null) {
      const options: AxiosRequestConfig = { headers: { token: Auth.getToken() } };
      authorization<UserDO>('myinfo', undefined, options).then((resp: UserDO) => {
        // Neccessary to authenticate user to store access data
        Auth.authenticateUser(resp);
      }).catch((err: any) => { console.error(err); });
    }
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  private login = (username: string, password: string): Promise<UserDO> => {
    const options: AxiosRequestConfig = { headers: { token: Auth.getToken() } };
    const data: URLSearchParams = new URLSearchParams();
    data.append('lang', navigator.language.split('-')[0]);
    data.append('username', username);
    data.append('password', password);

    return authorization<UserDO>('login', data, options);
  };

  public handleFormSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const { username, password } = this.state;

    if (username && password) {
      this.loadingStart();
      this.login(username, password)?.then((resp: UserDO) => {
        this.onLoginSuccess(resp);
      }).catch((error: any) => {
        this.onLoginFailed(error);
      }).finally(() => {
        this.loadingFinish();
      });
    } else {
      messageError('Error', 'Missing username or password');
    }
  };

  public handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const { name, type, value, checked } = e.target;
    this.setLocalState({
      [name]: type === 'checkbox' ? checked : value,
    });
  }

  public onLoginSuccess = (user: UserDO): void => {
    if (!user) return;
    Auth.authenticateUser(user);
  };

  public onLoginFailed = (error: any): void => {
    if (error.message) {
      messageError('Error', error.message);
    } else {
      messageError('Error', (error?.code === 401 ? 'Unauthorized Access' : 'Unknown error'));
    }
  };

  public loadingStart = (): void => {
    this.setLocalState({ loading: true, });
  };

  public loadingFinish = (): void => {
    this.setLocalState({ loading: false, });
  };

  public render(): JSX.Element {
    const { loading, username, password } = this.state;

    if (this.props.user !== undefined) {
      return <Redirect to="/" />;
    }

    const site: string = window.location.hostname.split('.')[0];
    document.title = `EntropySmart - ${site.toUpperCase()}`;

    return (
      <div id="extr-page">
        <div id="main" role="main" className="animated fadeInDown">
          <div id="content" className="container login-layer">
            <div className="col-xs-12 col-sm-6 col-md-6 col-lg-3 login-box">
              <form id="login-form" className="smart-form" onSubmit={this.handleFormSubmit}>
                <header>
                  <div id="login-logo">
                    <b>{site}</b>
                    <br />by<br />
                    <img src="assets/img/logos/logo_negativo.png" alt="EntropySmart" />
                    <br />
                    <b>EntropySmart-IOT</b> - {currentVersion}
                  </div>
                </header>
                <fieldset>
                  <section>
                    <label className="input">
                      <input
                        type="email"
                        name="username"
                        value={username}
                        placeholder="Email"
                        onChange={this.handleChange}
                      />
                    </label>
                  </section>
                  <section>
                    <label className="input">
                      <input
                        type="password"
                        name="password"
                        value={password}
                        placeholder="Password"
                        onChange={this.handleChange}
                      />
                    </label>
                  </section>
                </fieldset>
                <footer>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading && <i className="fa fa-refresh fa-spin login-loader" />} Log in
                  </button>
                </footer>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState, ownProps: Props): Props => {
  return {
    ...ownProps,
    user: _.get(state, ['auth', 'user'], undefined),
  };
};

export default withRouter(connect(mapStateToProps)(Login));
